import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Country,
  Currency,
  UserDepositMethod,
  UsersSelf,
} from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import {
  methodSelectionRadioControl,
  PaymentsUtils,
} from '@freelancer/payments-utils';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { SelectItem } from '@freelancer/ui/select';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import {
  DepositMethodApi,
  DepositRequestApi,
} from 'api-typings/payments/payments';
import * as Rx from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { LatinAmericaFormFields } from '../../../../common/latin-america-form/latin-america-form.component';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData, GenericPayload } from '../../../../common/types';
import { parseDepositAPIError } from '../../payments.helpers';

@Component({
  selector: 'app-boleto-method',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="id"
      [sourceDisplayMode]="sourceDisplayMode"
    >
      <app-source-template-header>
        <app-source-template-header-title
          i18n="Boleto Bancario - name of the provider"
        >
          Boleto Bancário
        </app-source-template-header-title>
        <app-source-template-logo
          [image]="boletoLogo"
        ></app-source-template-logo>
      </app-source-template-header>
      <app-source-template-body>
        <app-latin-america-form
          [countriesList]="countriesList$ | flAsync"
          [id]="id"
          [userInfo]="userInfo$ | flAsync"
          (genericFormGroup)="registerChildForm($event)"
        ></app-latin-america-form>
      </app-source-template-body>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./boleto-method.component.scss'],
})
export class BoletoMethodComponent implements OnInit, OnDestroy {
  IconColor = IconColor;
  IconSize = IconSize;
  PictureDisplay = PictureDisplay;
  TooltipPosition = TooltipPosition;
  SourceDisplayMode = SourceDisplayMode;

  boletoLogo: PictureImage = {
    alt: 'boleto',
    src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.BOLETO),
  };
  countriesList$: Rx.Observable<readonly SelectItem[]>;
  formGroup$: Rx.Observable<FormGroup>;
  methodFormGroup: FormGroup;
  stateSelectOptions: readonly SelectItem[];
  private subscriptions: readonly Rx.Subscription[] = [];

  @Input() countries$: Rx.Observable<readonly Country[]>;
  @Input() genericPayload: GenericPayload;
  @Input() depositFormGroup: FormGroup;
  @Input() group: FormGroup;
  @Input() id: string;
  @Input() userInfo$: Rx.Observable<UsersSelf>;
  @Input() depositMethod$: Rx.Observable<UserDepositMethod>;
  @Input() sourceDisplayMode: SourceDisplayMode;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  constructor(
    private paymentsMessaging: PaymentsMessagingService,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    const selected$ = this.group.valueChanges.pipe(
      startWith(this.group.value),
      map(value => value[methodSelectionRadioControl] === this.id),
      filter(selected => selected),
    );

    const emitPaymentCurrency$ = Rx.combineLatest([
      selected$,
      this.depositMethod$,
    ]).pipe(
      map(([_, methodData]) => {
        this.paymentCurrencySelected.emit(methodData.defaultCurrency);
      }),
    );
    this.subscriptions = [
      ...this.subscriptions,
      emitPaymentCurrency$.subscribe(),
    ];

    this.methodFormGroup = new FormGroup({});
    this.countriesList$ = this.countries$.pipe(
      map(countries =>
        countries
          .filter(country => country.code === 'BR')
          .map(country => ({
            displayText: country.name,
            value: country.code,
          })),
      ),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  registerChildForm(event: FormGroup) {
    this.methodFormGroup.addControl('boletoFormControl', event);
    this.paymentsUtils.registerFormGroup(
      this.methodFormGroup,
      this.depositFormGroup,
      this.id,
    );
  }

  handleDepositDataChanges(depositData: DepositData) {
    if (depositData) {
      const payload: DepositRequestApi = {
        ...this.genericPayload,
        boleto_info: {
          ...this.methodFormGroup.value.boletoFormControl,
        },
        deposit_method: DepositMethodApi.BOLETO,
        amount: depositData.depositAmount,
        currency_id: depositData.depositCurrency.id,
        country_code: this.methodFormGroup.value.boletoFormControl[
          LatinAmericaFormFields.COUNTRY_CODE
        ],
      };
      this.deposits
        .deposit(payload)
        .toPromise()
        .then(response => {
          if (response.status === 'success') {
            this.paymentsMessaging.pushResult({
              paymentsMessage: `
                You have successfully created your Boleto Bancário, please double check that all details are correct.
                Your Boleto Bancário needs to be paid before ${response.result.due_date} (Sao Paulo BR Local Time) at ATMs, branch facilities
                and internet banking of any Bank, Post Office, Lottery Agent and some supermarkets.
              `,
              cta: `Here's the boleto link.`,
              ctaLink: response.result.url,
              paymentsStatus: PaymentsResultStatus.PENDING,
            });
          } else {
            this.paymentsMessaging.pushError({
              errorCode: response.errorCode,
              errorType: PaymentsErrorType.ERROR,
              eventId: response.requestId,
            });
          }
        })
        .catch(err => {
          this.paymentsMessaging.pushError(parseDepositAPIError(err));
        });
    }
  }
}
